import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaUserSecret, FaUsers, FaTimes, FaChevronDown } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const GroupOwnerPanel = () => {
  const { 
    getCurrentUser, 
    isGroupOwner, 
    isGroupOwnerMode, 
    originalGroupOwner, 
    impersonatedGroupMember, 
    impersonateGroupMember, 
    stopGroupMemberImpersonation,
    isAdmin,
    originalAdminUser,
    api 
  } = useAuth();
  
  const currentUser = getCurrentUser();
  
  const [isOpen, setIsOpen] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // 어드민이 그룹 관리자로 가장했는지 확인
  const isAdminImpersonatingGroupOwner = originalAdminUser && isGroupOwner();

  // 패널 콘텐츠 참조 생성
  const groupPanelRef = useRef(null);

  // 사용자의 그룹 목록 조회
  const fetchUserGroups = async () => {
    if (!isGroupOwner()) return; // 그룹 관리자 아니면 API 호출 안함
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.get('/api/v1/groups/');
      
      // 응답 데이터 구조 확인 및 처리
      let groups = response.data;
      if (Array.isArray(response.data.groups)) {
        groups = response.data.groups;
      }
      
      // 그룹 데이터 중 본인이 관리자인 그룹만 필터링
      const ownedGroups = groups.filter(group => {
        const isCurrentUserOwner = String(group.owner_user) === String(currentUser.id);
        return isCurrentUserOwner;
      });
      
      setUserGroups(ownedGroups);
      
      // 그룹이 있으면 첫 번째 그룹 선택
      if (ownedGroups.length > 0) {
        setSelectedGroupId(ownedGroups[0].id);
      }
    } catch (error) {
      console.error('그룹 목록 조회 실패:', error);
      setError('그룹 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 그룹 멤버 목록 조회
  const fetchGroupMembers = async (groupId) => {
    if (!isGroupOwner() || !groupId) return; // 그룹 관리자 아니면 API 호출 안함
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.get(`/api/v1/groups/${groupId}/stats`);
      
      // 응답 데이터 구조 확인 및 처리
      let members = response.data;
      if (Array.isArray(response.data.members)) {
        members = response.data.members;
      }
      
      // 멤버 데이터 정규화 - 자신 제외
      const normalizedMembers = members
        .filter(member => String(member.user_id) !== String(currentUser.id))
        .map(member => ({
          id: member.user_id,
          email: member.email,
          nickname: member.nickname || member.name || `사용자 ${member.user_id}`,
          name: member.name
        }));
      
      setGroupMembers(normalizedMembers);
    } catch (error) {
      console.error('그룹 멤버 목록 조회 실패:', error);
      setError('멤버 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 컴포넌트 마운트 시 사용자의 그룹 목록 조회 - Hook은 항상 조건문 이전에 위치해야 함
  useEffect(() => {
    fetchUserGroups();
  }, []);

  // 선택된 그룹이 변경될 때 그룹 멤버 목록 조회 - Hook은 항상 조건문 이전에 위치해야 함 
  useEffect(() => {
    if (selectedGroupId) {
      fetchGroupMembers(selectedGroupId);
    }
  }, [selectedGroupId]);

  // 패널 바깥 클릭 감지 useEffect 추가
  useEffect(() => {
    // mousedown 이벤트 핸들러 함수
    const handleClickOutside = (event) => {
      // 패널 참조(ref)가 있고, 클릭된 요소가 패널 콘텐츠 내부에 포함되지 않은 경우
      if (groupPanelRef.current && !groupPanelRef.current.contains(event.target)) {
        // 예외: 그룹 오너 패널 버튼 자체를 클릭한 경우는 무시 (버튼이 토글 역할)
        const groupButton = document.querySelector('[data-el="group-button"]'); // 버튼에 data-el 속성 추가 필요
        if (groupButton && groupButton.contains(event.target)) {
          return;
        }
        setIsOpen(false); // 패널 닫기
      }
    };

    // 패널이 보여질 때만 이벤트 리스너 추가
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // cleanup 함수: 컴포넌트 언마운트 또는 패널 닫힐 때 리스너 제거
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]); // isOpen 상태가 변경될 때마다 effect 재실행

  // 사용자가 그룹 관리자가 아닌 경우 렌더링 안함
  if (!isGroupOwner()) {
    return null;
  }

  // 가장하기 함수
  const handleImpersonate = async (userId) => {
    try {
      // Context의 impersonateGroupMember 함수 사용
      const success = await impersonateGroupMember(userId);
      
      if (success) {
        // 가장 성공 시 패널 닫기 및 상태 초기화
        setIsOpen(false);
        setDropdownOpen(false);
        
        // 사용자에게 알림
        // alert('그룹 멤버 가장 모드를 시작했습니다.');
        
        return true;
      } else {
        setError('그룹 멤버 가장에 실패했습니다.');
        return false;
      }
    } catch (error) {
      console.error('그룹 멤버 가장 중 오류 발생:', error);
      setError('그룹 멤버 가장에 실패했습니다.');
      return false;
    }
  };

  // 가장 중지 함수
  const handleStopImpersonation = async () => {
    try {
      // Context의 stopGroupMemberImpersonation 함수 사용
      const success = await stopGroupMemberImpersonation();
      
      if (success) {
        // 가장 중지 성공 시 패널 닫기
        setIsOpen(false);
        setDropdownOpen(false);
        return true;
      } else {
        setError('가장 모드 해제에 실패했습니다.');
        return false;
      }
    } catch (error) {
      console.error('가장 모드 중지 중 오류 발생:', error);
      setError('가장 모드 해제에 실패했습니다.');
      return false;
    }
  };

  // 그룹 선택 처리
  const handleGroupSelect = (groupId) => {
    setSelectedGroupId(groupId);
  };

  return (
    <GroupPanelContainer $isAdminUser={isAdmin}>
      {/* 어드민이 그룹 관리자로 가장한 경우 어드민 패널 버튼은 제거 - 어드민 패널이 자동으로 표시됨 */}

      {/* 접힌 상태에서 보이는 버튼 */}
      <GroupButton onClick={() => setIsOpen(!isOpen)} data-el="group-button">
        {isGroupOwnerMode ? <FaUserSecret /> : <FaUsers />}
      </GroupButton>

      {/* 열린 상태의 패널 */}
      {isOpen && (
        <GroupPanelContent ref={groupPanelRef}>
          <PanelHeader>
            <PanelTitle>그룹 관리자 모드</PanelTitle>
            <CloseButton onClick={() => setIsOpen(false)}>
              <FaTimes />
            </CloseButton>
          </PanelHeader>

          {/* 어드민이 그룹 관리자로 가장한 경우 표시
          {isAdminImpersonatingGroupOwner && (
            <AdminSection>
              <AdminStatusText>
                <strong>{originalAdminUser?.nickname || '관리자'}</strong>님이 그룹 관리자로 가장 중입니다.
              </AdminStatusText>
            </AdminSection>
          )} */}

          {/* 현재 상태 표시 */}
          <StatusSection>
            {isGroupOwnerMode ? (
              <>
                <StatusText>
                  <strong>{originalGroupOwner?.nickname || originalGroupOwner?.name || originalGroupOwner?.email || '그룹 관리자'}</strong>님이 
                  <strong>{impersonatedGroupMember?.nickname || impersonatedGroupMember?.name || impersonatedGroupMember?.email || '사용자'}</strong>님으로 이용 중
                </StatusText>
                <BackButtonHighlight onClick={handleStopImpersonation}>
                  멤버 모드 해제하기
                </BackButtonHighlight>
                <RefreshInfoText>
                  해제 후 자동으로 새로고침됩니다.
                </RefreshInfoText>
              </>
            ) : (
              <StatusText>
                <strong>{currentUser?.nickname || currentUser?.name || currentUser?.email || '그룹 관리자'}</strong>님은 그룹 관리자입니다
              </StatusText>
            )}
          </StatusSection>

          {/* 사용자 선택 영역 */}
          <MemberSection>
            <SectionTitle>멤버 모드 시작하기</SectionTitle>
            
            {isGroupOwnerMode ? (
              <DisabledMemberMessage>
                멤버 모드 중에는 다른 멤버 선택이 비활성화됩니다.
                멤버 모드를 해제한 후 다시 시도해주세요.
              </DisabledMemberMessage>
            ) : (
              <>
                {/* 그룹 선택 드롭다운 */}
                {userGroups.length > 1 && (
                  <GroupSelector>
                    <GroupSelectorLabel>그룹 선택:</GroupSelectorLabel>
                    <GroupDropdown>
                      {userGroups.map(group => (
                        <GroupOption 
                          key={group.id} 
                          selected={selectedGroupId === group.id}
                          onClick={() => handleGroupSelect(group.id)}
                        >
                          {group.group_name}
                        </GroupOption>
                      ))}
                    </GroupDropdown>
                  </GroupSelector>
                )}
                
                {error && <ErrorMessage>{error}</ErrorMessage>}
                
                {isLoading ? (
                  <LoadingText>멤버 목록 로딩 중...</LoadingText>
                ) : groupMembers.length > 0 ? (
                  <MembersDropdownContainer>
                    <MembersDropdownHeader onClick={() => setDropdownOpen(!dropdownOpen)}>
                      <span>멤버 선택</span>
                      <FaChevronDown />
                    </MembersDropdownHeader>
                    
                    {dropdownOpen && (
                      <MembersList>
                        {groupMembers.map(member => (
                          <MemberItem key={member.id} onClick={() => handleImpersonate(member.id)}>
                            <MemberName>{member.nickname || member.name || '사용자 ' + member.id}</MemberName>
                            {member.email && <MemberEmail>{member.email}</MemberEmail>}
                          </MemberItem>
                        ))}
                      </MembersList>
                    )}
                  </MembersDropdownContainer>
                ) : (
                  <NoMembersMessage>
                    멤버가 없거나 로드할 수 없습니다.
                  </NoMembersMessage>
                )}
              </>
            )}
          </MemberSection>
        </GroupPanelContent>
      )}
    </GroupPanelContainer>
  );
};

// 스타일 컴포넌트
const GroupPanelContainer = styled.div`
  position: fixed;
  bottom: ${({ $isAdminUser }) => $isAdminUser ? '20px' : '20px'}; /* 어드민 패널 존재 시 위로 올라가도록 */
  left: 20px;
  z-index: 1000; /* 어드민 패널보다 아래에 위치하도록 z-index 조정 */
`;

// 어드민 패널 버튼 제거 - 불필요함

const GroupButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color:  #673ab7; /* 보라색 - 관리자 색상 */
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: #673ab7; }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(72, 187, 120, 0.3);
  }
`;

const GroupPanelContent = styled.div`
  position: absolute;
  bottom: 60px;
  left: 0;
  width: 320px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  overflow: hidden;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.5rem;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #673ab7;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #4A5568;
  }
  
  &:focus {
    outline: none;
  }
`;

// 어드민이 그룹 관리자로 가장한 경우 표시되는 섹션
const AdminSection = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #E9D8FD; /* 보라색 배경 - 관리자 테마 */
  border-radius: 4px;
  border-left: 4px solid #9F7AEA;
`;

const AdminStatusText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #553C9A;
  
  strong {
    color: #44337A;
  }
`;

const StatusSection = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #F7FAFC;
  border-radius: 4px;
`;

const StatusText = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  color: #4A5568;
  
  strong {
    color: #2D3748;
  }
`;

const BackButtonHighlight = styled.button`
  width: 100%;
  padding: 0.5rem;
  background-color: #FED7D7;
  color: #E53E3E;
  border: 1px solid #FC8181;
  border-radius: 4px;
  font-weight: 600;
  margin-top: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #FC8181;
    color: white;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.3);
  }
`;

const RefreshInfoText = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 0.75rem;
  color: #718096;
  text-align: center;
`;

const MemberSection = styled.div`
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2D3748;
`;

const GroupSelector = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
`;

const GroupSelectorLabel = styled.span`
  font-size: 0.875rem;
  color: #4A5568;
  margin-right: 0.5rem;
`;

const GroupDropdown = styled.div`
  flex: 1;
  border: 1px solid #E2E8F0;
  border-radius: 4px;
  overflow: hidden;
`;

const GroupOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  background-color: ${props => props.selected ? '#EBF8FF' : 'white'};
  
  &:hover {
    background-color: ${props => props.selected ? '#EBF8FF' : '#F7FAFC'};
  }
`;

const MembersDropdownContainer = styled.div`
  border: 1px solid #E2E8F0;
  border-radius: 4px;
  overflow: hidden;
`;

const MembersDropdownHeader = styled.div`
  padding: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #F7FAFC;
  cursor: pointer;
  
  &:hover {
    background-color: #EDF2F7;
  }
`;

const MembersList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #F7FAFC;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #CBD5E0;
    border-radius: 3px;
  }
`;

const MemberItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  border-top: 1px solid #E2E8F0;
  
  &:hover {
    background-color: #F7FAFC;
  }
`;

const MemberName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #2D3748;
`;

const MemberEmail = styled.div`
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;
`;

const NoMembersMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: #718096;
  font-size: 0.875rem;
  background-color: #F7FAFC;
  border-radius: 4px;
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background-color: #FED7D7;
  color: #C53030;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 1rem;
  color: #718096;
  font-size: 0.875rem;
`;

const DisabledMemberMessage = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 12px;
  margin: 10px 0;
  font-size: 14px;
  color: #6c757d;
  text-align: center;
  line-height: 1.5;
`;

export default GroupOwnerPanel; 